/**
 * Story 053 — TanStack Query hooks for the admin surface.
 *
 * Re-uses the small `authFetch` helper from the Story 052 account
 * feature (same fetch shape, same auth headers, same error envelope).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "../../account/queries/authFetch.js";
import { adminKeys } from "./keys.js";
import type {
  AdminUser,
  InviteUserBody,
  ListAuditParams,
  ListInvitationsParams,
  ListUsersParams,
  PaginatedAuditEvents,
  PaginatedInvitations,
  PaginatedUsers,
  Role,
} from "./types.js";

function listUsersUrl(params: ListUsersParams): string {
  const sp = new URLSearchParams();
  sp.set("page", String(params.page ?? 1));
  sp.set("pageSize", String(params.pageSize ?? 25));
  if (params.search && params.search.length > 0)
    sp.set("search", params.search);
  for (const r of params.role ?? []) sp.append("role", r);
  for (const s of params.status ?? []) sp.append("status", s);
  if (params.sort) sp.set("sort", params.sort);
  return `/users?${sp.toString()}`;
}

export function useAdminUsersQuery(params: ListUsersParams) {
  return useQuery<PaginatedUsers>({
    queryKey: adminKeys.usersList(params),
    queryFn: () => authFetch<PaginatedUsers>(listUsersUrl(params)),
    staleTime: 30_000,
    retry: false,
  });
}

export function useAdminUserQuery(id: string | null | undefined) {
  return useQuery<AdminUser>({
    queryKey: adminKeys.user(id ?? "__none__"),
    queryFn: () => authFetch<AdminUser>(`/users/${id ?? ""}`),
    enabled: typeof id === "string" && id.length > 0,
    staleTime: 30_000,
    retry: false,
  });
}

export interface UpdateRoleArgs {
  id: string;
  role: Role;
}

export function useUpdateUserRoleMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }: UpdateRoleArgs) =>
      authFetch<{ ok: true }>(`/users/${id}/role`, {
        method: "PATCH",
        body: { role },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

/**
 * SB2-W-01 — tenant-scoped in-product role change.
 *
 * Wire: PATCH /api/auth/v1/admin/users/{userId}/role
 *       (contract auth/v1@5.2.0, operationId `changeTenantUserRole`).
 *
 * Optimistic update + rollback on 403:
 *   - onMutate snapshots the current AdminUser row and patches the
 *     query cache with the requested role so the drawer + list
 *     reflect the change instantly.
 *   - onError restores the snapshot — used by AC8 to surface the
 *     `tenant_admin_promotion_forbidden` toast without flipping the
 *     UI to the forbidden role.
 *
 * The toast wiring is the caller's responsibility (the drawer reads
 * `e.body.reason` and dispatches the verbatim reason string). This
 * hook stays caller-agnostic so the mutation can be reused by the
 * inline RoleChangeButton on the list page.
 */
export function useChangeTenantUserRoleMutation() {
  const qc = useQueryClient();
  return useMutation<
    AdminUser,
    Error & { status?: number; body?: { reason?: string } },
    UpdateRoleArgs,
    { previous: AdminUser | undefined }
  >({
    mutationFn: ({ id, role }) =>
      authFetch<AdminUser>(`/admin/users/${id}/role`, {
        method: "PATCH",
        body: { role },
      }),
    onMutate: async ({ id, role }) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic
      // patch.
      await qc.cancelQueries({ queryKey: adminKeys.user(id) });
      const previous = qc.getQueryData<AdminUser>(adminKeys.user(id));
      if (previous) {
        qc.setQueryData<AdminUser>(adminKeys.user(id), {
          ...previous,
          role,
        });
      }
      return { previous };
    },
    onError: (_err, { id }, ctx) => {
      // Roll back to the snapshot. The drawer's local form-state
      // (`selectedRole`) follows `user.role` via useEffect, so the
      // restored cache value cascades back to the dropdown.
      if (ctx?.previous) {
        qc.setQueryData<AdminUser>(adminKeys.user(id), ctx.previous);
      }
    },
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: adminKeys.user(id) });
      void qc.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

/**
 * SB2-W-01 — cross-tenant in-product role change for platform staff.
 *
 * Wire: PATCH /api/auth/v1/platform-admin/users/{userId}/role
 *       (contract auth/v1@5.2.0, operationId `changeAnyUserRole`).
 *
 * No `X-Tenant-ID` header — the endpoint is intentionally tenant-
 * agnostic. The `authFetch` helper still sets the header by default;
 * the BE controller ignores it for this path. Optimistic-rollback
 * shape matches `useChangeTenantUserRoleMutation` so the platform-
 * admin Users page can reuse the same drawer body.
 */
export function useChangeAnyUserRoleMutation() {
  const qc = useQueryClient();
  return useMutation<
    AdminUser,
    Error & { status?: number; body?: { reason?: string } },
    UpdateRoleArgs,
    { previous: AdminUser | undefined }
  >({
    mutationFn: ({ id, role }) =>
      authFetch<AdminUser>(`/platform-admin/users/${id}/role`, {
        method: "PATCH",
        body: { role },
      }),
    onMutate: async ({ id, role }) => {
      await qc.cancelQueries({ queryKey: adminKeys.user(id) });
      const previous = qc.getQueryData<AdminUser>(adminKeys.user(id));
      if (previous) {
        qc.setQueryData<AdminUser>(adminKeys.user(id), {
          ...previous,
          role,
        });
      }
      return { previous };
    },
    onError: (_err, { id }, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData<AdminUser>(adminKeys.user(id), ctx.previous);
      }
    },
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: adminKeys.user(id) });
      void qc.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useBlockUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      authFetch<undefined>(`/users/${id}/block`, { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useUnblockUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      authFetch<undefined>(`/users/${id}/unblock`, { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useRemoveUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      authFetch<undefined>(`/users/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useForcePasswordResetMutation() {
  return useMutation({
    mutationFn: (id: string) =>
      authFetch<undefined>(`/users/${id}/force-password-reset`, {
        method: "POST",
      }),
  });
}

export function useInviteUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    // BUG-2 / SB6-INVITE-01 — route through createInvitation, not
    // createUser. The two BE paths look superficially similar but only
    // POST /invitations runs the SB5-A Zitadel dispatch chain
    // (createHumanUser → addUserGrant → inviteUserCode) AND inserts
    // into `public.invitations` so the Admin → Invitations page shows
    // the pending row. The legacy POST /users path called
    // `zitadel.sendInvitation` which is a no-op in the local stub
    // wiring — that is why Christian saw a success toast but no email
    // in mailhog and an empty invitations list.
    mutationFn: (body: InviteUserBody) =>
      authFetch<unknown>("/invitations", {
        method: "POST",
        body: {
          email: body.email,
          role: body.role,
          // BE field is `message`; FE-side label is `welcomeMessage`.
          ...(body.welcomeMessage ? { message: body.welcomeMessage } : {}),
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.users() });
      void qc.invalidateQueries({ queryKey: adminKeys.invitations() });
    },
  });
}

export function useInvitationsQuery(params: ListInvitationsParams) {
  return useQuery<PaginatedInvitations>({
    queryKey: adminKeys.invitationsList(params),
    queryFn: () => {
      const sp = new URLSearchParams();
      sp.set("page", String(params.page ?? 1));
      sp.set("pageSize", String(params.pageSize ?? 25));
      return authFetch<PaginatedInvitations>(`/invitations?${sp.toString()}`);
    },
    staleTime: 30_000,
    retry: false,
  });
}

export function useResendInvitationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      authFetch<undefined>(`/invitations/${id}/resend`, { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.invitations() });
    },
  });
}

export function useRevokeInvitationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      authFetch<undefined>(`/invitations/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.invitations() });
    },
  });
}

export function useAuditLogQuery(params: ListAuditParams) {
  return useQuery<PaginatedAuditEvents>({
    queryKey: adminKeys.auditList(params),
    queryFn: () => {
      const sp = new URLSearchParams();
      sp.set("page", String(params.page ?? 1));
      sp.set("pageSize", String(params.pageSize ?? 25));
      // R0627-13 — the BE filters on `eventType` (a single string;
      // `GET /api/auth/v1/audit-log`, contract auth/v1), NOT `action`.
      // Sending `action` was silently ignored, so the list came back
      // unfiltered. Send the selected event type as `eventType`. The
      // endpoint takes one value (`ae.event_type = $N`), so when several
      // actions are selected we filter by the first.
      const [eventType] = params.action ?? [];
      if (eventType) sp.set("eventType", eventType);
      if (params.actor) sp.set("actor", params.actor);
      if (params.target) sp.set("target", params.target);
      if (params.from) sp.set("from", params.from);
      if (params.to) sp.set("to", params.to);
      return authFetch<PaginatedAuditEvents>(`/audit-log?${sp.toString()}`);
    },
    staleTime: 30_000,
    retry: false,
  });
}

export type Status = "active" | "invited" | "blocked" | "deleted";

/** Derive the displayable status from a user row's raw fields. */
export function deriveUserStatus(u: AdminUser): Status {
  if (u.deletedAt !== null) return "deleted";
  if (!u.isActive) return "blocked";
  if (u.acceptedAt === null && u.invitedAt !== null) return "invited";
  return "active";
}
